import {
  Add,
  ChevronLeft,
  ChevronRight,
  Close,
  Done,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { JSX, useEffect, useReducer, useRef, useState } from "react";
import {
  getRfpCoveragesEndpoint,
  getRfpCoveragesQueryKey,
  RfpCoveragesResponse,
  useApi,
} from "@platform/api";
import { Button, Typography } from "@platform/design-system";

import useToastNotificationService from "../../../../hooks/useToastNotificationService";
import { extractErrorMessage } from "../../../../utils";
import { CoverageGrid, CoverageType } from "../coverageGrid";
import { getFinalDecisionsCacheKey } from "../FinalDecisions/useFinalDecisionApi";
import RfpCoverageForm from "../rfpCoverageForm";

import {
  CoverageDetails,
  CoveragesMutation,
  ProductElement,
  ProductsResponse,
  SelectedCoverageDetails,
} from "./types";
import { reducer } from "./reducer";
import { COVERAGE_STEP, FORM_STEP, initialState } from "./constants";
import {
  areAllCoveragesValid,
  createNextStepDetails,
  getAllSelectedCoverages,
  getCurrentCoverageIndex,
  parseProduct,
  productsResponseSelect,
} from "./helpers";

type Props = {
  onClose: VoidFunction;
  currentData: RfpCoveragesResponse;
  rfpId: string;
  ecoRfpId?: string;
};

const RfpCoverageEditModal = ({
  rfpId,
  onClose,
  currentData,
  ecoRfpId,
}: Props): JSX.Element => {
  const axiosInstance = useApi();
  const { createToastNotification } = useToastNotificationService();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const allSelectedCoverages = getAllSelectedCoverages(state.selectedCoverages);

  const {
    data: allCoverages,
    isLoading: isLoadingCoverages,
    isFetching,
  } = useQuery<ProductsResponse, Error>({
    queryKey: ["rfp", "coverages", "edit", rfpId],
    queryFn: async () => {
      const response = await axiosInstance.get<ProductsResponse>(
        `brokerportal/v1/rfp/${rfpId}/coverages/edit`,
      );

      const clientProductSelected = response.data.clientProducts.filter(
        (item) =>
          currentData.currentCoverages?.some((curr) => curr.name === item.name),
      );

      const productSelected = response.data.products.filter((item) =>
        currentData.newCoverages?.some((curr) => curr.name === item.name),
      );

      dispatch({
        type: "UPDATE_SELECTED_COVERAGES",
        payload: {
          clientProducts: clientProductSelected.map((item) =>
            parseProduct({
              product: item,
              isCurrentCoverage: true,
              currentProducts: currentData.currentCoverages,
            }),
          ),
          products: productSelected.map((item) =>
            parseProduct({
              product: item,
              currentProducts: currentData.newCoverages,
            }),
          ),
        },
      });

      return response.data;
    },
    select: productsResponseSelect(),
    refetchOnWindowFocus: false,
  });

  const isLoading = isFetching || isLoadingCoverages;

  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, CoveragesMutation>({
    mutationFn: (coverages) =>
      axiosInstance.patch(getRfpCoveragesEndpoint(rfpId), {
        coverages,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getRfpCoveragesQueryKey(rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: getFinalDecisionsCacheKey(rfpId),
      });

      // Also invalidate the BTX RFP cache to ensure Build 2.0 gets fresh data
      // when user navigates back after adding/removing LOCs
      if (ecoRfpId) {
        queryClient.invalidateQueries({
          queryKey: ["rfp", ecoRfpId],
        });
        queryClient.invalidateQueries({
          queryKey: ["benefit-classes", ecoRfpId],
        });
        queryClient.invalidateQueries({
          queryKey: ["rfp-coverages", ecoRfpId],
        });
        queryClient.invalidateQueries({
          queryKey: ["coverage-summary", ecoRfpId],
        });
      }

      onClose();
      createToastNotification({
        message: "Coverages updated successfully",
        severity: "success",
      });
    },
    onError: (error) => {
      createToastNotification({
        message: extractErrorMessage(error) as string,
        severity: "error",
      });
    },
  });

  const handleClose = (): void => {
    if (
      state.currentStep === FORM_STEP &&
      (state.selectedCoverages.products.length > 0 ||
        state.selectedCoverages.clientProducts.length > 0)
    ) {
      setConfirmDialogOpen(true);
    } else {
      onClose();
    }
  };

  const handleCancelClose = (): void => {
    setConfirmDialogOpen(false);
  };

  const handleToggleCoverage = (
    coverage: ProductElement,
    type: CoverageType,
  ): void => {
    dispatch({
      type: "TOGGLE_COVERAGE",
      payload: { coverage, type },
    });
  };

  const handleSelectCoverage = (coverage: CoverageDetails | null): void => {
    dispatch({
      type: "SET_SELECTED_COVERAGE_DETAILS",
      payload: createNextStepDetails(coverage),
    });
  };

  const handleSetCurrentPlanIndex = (planIndex: number): void => {
    dispatch({ type: "SET_CURRENT_PLAN_INDEX", payload: planIndex });
  };

  const handleCoverageChange = (
    coverage: CoverageDetails,
    currentPlanKey: number,
    field: string,
  ): void => {
    dispatch({
      type: "UPDATE_COVERAGE_DETAILS",
      payload: { coverage, currentPlanKey, field },
    });
  };

  const handleSubmit = (): void => {
    const data = allSelectedCoverages.map((c) => ({
      productId: c.id,
      details: c.details,
      isCurrentCoverage: c.isCurrentCoverage,
    }));
    mutation.mutate(data);
  };

  const currentCoverageIndex = getCurrentCoverageIndex(
    allSelectedCoverages,
    state.currentCoverageDetails?.id,
  );
  const isPreviousDisabled =
    allSelectedCoverages.length <= 1 || currentCoverageIndex <= 0;
  const isNextDisabled =
    allSelectedCoverages.length <= 1 ||
    currentCoverageIndex >= allSelectedCoverages.length - 1;

  const isNextButtonDisabled = isLoading || allSelectedCoverages.length === 0;

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      aria-labelledby="coverage-edit-modal-title"
      aria-describedby="coverage-edit-modal-description"
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle id="coverage-edit-modal-title">
        Add Coverages To Quote
      </DialogTitle>
      {state.currentStep === COVERAGE_STEP && (
        <DialogContent>
          <SelectionStep
            allCoverages={allCoverages}
            isLoading={isLoading}
            selectedCoverages={state.selectedCoverages}
            onToggleCoverage={handleToggleCoverage}
          />
        </DialogContent>
      )}
      {state.currentStep === FORM_STEP && (
        <DialogContent sx={{ display: "flex" }}>
          <FormStep
            selectedCoverages={state.selectedCoverages}
            allCoverages={allCoverages}
            onToggleCoverage={handleToggleCoverage}
            currentCoverage={state.currentCoverageDetails}
            currentPlanIndex={state.currentPlanIndex}
            onSelectCoverage={handleSelectCoverage}
            onCoverageChange={handleCoverageChange}
            onSetCurrentPlanIndex={handleSetCurrentPlanIndex}
          />
        </DialogContent>
      )}

      <DialogActions>
        {state.currentStep === FORM_STEP ? (
          <>
            <Button
              variant="tertiary"
              onClick={() =>
                dispatch({ type: "NAVIGATE_TO_PREVIOUS_COVERAGE" })
              }
              disabled={isPreviousDisabled}
              startIcon={<ChevronLeft />}
            >
              Previous
            </Button>

            <Button
              variant="tertiary"
              onClick={() => dispatch({ type: "NAVIGATE_TO_NEXT_COVERAGE" })}
              disabled={isNextDisabled}
              endIcon={<ChevronRight />}
            >
              Next
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={mutation.isPending}
              disabled={!areAllCoveragesValid(allSelectedCoverages)}
              onClick={handleSubmit}
            >
              Save Coverages
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={isNextButtonDisabled}
              onClick={() => dispatch({ type: "SET_STEP", payload: FORM_STEP })}
            >
              Next: Coverage Details
            </Button>
          </>
        )}
      </DialogActions>
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirm Close</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to close without saving your changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" onClick={handleCancelClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setConfirmDialogOpen(false);
              onClose();
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

const FormStep = ({
  selectedCoverages,
  allCoverages,
  currentCoverage,
  currentPlanIndex,
  onToggleCoverage,
  onSelectCoverage,
  onCoverageChange,
  onSetCurrentPlanIndex,
}: {
  selectedCoverages: SelectedCoverageDetails;
  allCoverages: ProductsResponse;
  currentCoverage: CoverageDetails | null;
  currentPlanIndex: number;
  onToggleCoverage: (coverage: ProductElement, type: CoverageType) => void;
  onSelectCoverage: (coverage: ProductElement | null) => void;
  onCoverageChange: (
    coverage: CoverageDetails,
    planKey: number,
    field: string,
  ) => void;
  onSetCurrentPlanIndex: (planKey: number) => void;
}): JSX.Element => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const coverageId = currentCoverage?.id;
  useEffect(() => {
    // If there is no coverageId, do not scroll
    if (!coverageId) return;

    // Scroll the parent container to the top when the form renders
    if (formContainerRef && formContainerRef.current) {
      formContainerRef.current.scrollTop = 0;
    }
  }, [coverageId, currentPlanIndex]); // Scroll when coverage or plan index changes

  return (
    <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
      <Paper sx={{ width: "30%", overflowY: "auto", maxHeight: "100%" }}>
        <CoverageMenu
          title="Current Coverages to Quote"
          selected={selectedCoverages}
          all={allCoverages}
          type="clientProducts"
          onToggleCoverage={onToggleCoverage}
          onSelectCoverage={onSelectCoverage}
          currentCoverage={currentCoverage}
        />
        <CoverageMenu
          title="New Coverages to Quote"
          selected={selectedCoverages}
          all={allCoverages}
          type="products"
          onToggleCoverage={onToggleCoverage}
          onSelectCoverage={onSelectCoverage}
          currentCoverage={currentCoverage}
        />
      </Paper>
      <Paper
        ref={formContainerRef}
        sx={{
          overflowY: "auto",
          maxHeight: "100%",
          paddingX: 2,
          paddingBottom: 2,
          width: "70%",
        }}
      >
        <RfpCoverageForm
          coverage={currentCoverage}
          onFormChange={onCoverageChange}
          currentPlanIndex={currentPlanIndex}
          onSetCurrentPlanIndex={onSetCurrentPlanIndex}
        />
      </Paper>
    </Box>
  );
};

const CoverageMenu = ({
  title,
  selected,
  all,
  onToggleCoverage,
  onSelectCoverage,
  currentCoverage,
  type,
}: {
  title: string;
  selected: SelectedCoverageDetails;
  all: ProductsResponse;
  onToggleCoverage: (coverage: ProductElement, type: CoverageType) => void;
  onSelectCoverage: (coverage: ProductElement | null) => void;
  currentCoverage: CoverageDetails | null;
  type: CoverageType;
}): JSX.Element | null => {
  const allItems = all?.[type] || [];
  const selectedItems = selected[type];

  if (selectedItems.length === 0 && allItems.length === 0) {
    return null;
  }

  return (
    <Box>
      <ListItem
        secondaryAction={
          allItems.length > 0 ? (
            <CoverageMenuButton
              coverages={allItems}
              onAdd={(coverage) => onToggleCoverage(coverage, type)}
            />
          ) : null
        }
      >
        <ListItemText
          primary={
            <Typography variant="header-large">
              <strong>{title}</strong>
            </Typography>
          }
        />
      </ListItem>
      {currentCoverage ? (
        <List>
          {selectedItems.map((item) => {
            const isSelected = currentCoverage.id === item.id;
            const isEnabled = !isSelected; // Allow navigation to any non-selected coverage
            return (
              <ListItemButton
                key={item.id}
                onClick={() => {
                  if (isEnabled) {
                    onSelectCoverage(item);
                  }
                }}
                selected={isSelected}
                sx={{
                  cursor: isEnabled ? "pointer" : "default",
                }}
                title={isSelected ? "" : `Switch to ${item.name} coverage`}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={2}>
                      {(isSelected ? currentCoverage.isValid : item.isValid) ? (
                        <Done
                          sx={({ palette }) => ({
                            color: palette.brandPrimary.main,
                            verticalAlign: "bottom",
                          })}
                        />
                      ) : (
                        <Box width={24} height={24} />
                      )}
                      <Typography sx={{ wordBreak: "break-word" }}>
                        {item.name}
                      </Typography>
                    </Box>
                  }
                />
                <ListItemIcon>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleCoverage(item, type);
                    }}
                  >
                    <Close />
                  </IconButton>
                </ListItemIcon>
              </ListItemButton>
            );
          })}
        </List>
      ) : null}
    </Box>
  );
};

const CoverageMenuButton = ({
  coverages,
  onAdd,
}: {
  coverages: ProductElement[];
  onAdd: (coverage: ProductElement) => void;
}): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleAdd = (coverage: ProductElement): void => {
    onAdd(coverage);
    handleClose();
  };

  return (
    <>
      <IconButton edge="end" aria-label="add" onClick={handleClick}>
        <Add />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {coverages.map((coverage) => (
          <MenuItem key={coverage.id} onClick={() => handleAdd(coverage)}>
            {coverage.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SelectionStep = ({
  allCoverages,
  isLoading,
  selectedCoverages,
  onToggleCoverage,
}: {
  allCoverages: ProductsResponse;
  isLoading: boolean;
  selectedCoverages: ProductsResponse;
  onToggleCoverage: (coverage: ProductElement, type: CoverageType) => void;
}): JSX.Element => {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper>
        <CoverageGrid
          title="Current Coverages"
          type="clientProducts"
          allSelected={selectedCoverages}
          all={allCoverages}
          onSelect={onToggleCoverage}
        />
      </Paper>
      <Paper>
        <CoverageGrid
          title="New Coverages"
          type="products"
          allSelected={selectedCoverages}
          all={allCoverages}
          onSelect={onToggleCoverage}
        />
      </Paper>
    </>
  );
};

export default RfpCoverageEditModal;
